import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { tripsApi } from '../../api/trips.api';
import { usersApi } from '../../api/users.api';
import { User } from '../../types/user';
import { useAuth } from '../../hooks/useAuth';
import { toast } from '../../utils/toast';

interface GenerateDemoTripsModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export const GenerateDemoTripsModal: React.FC<GenerateDemoTripsModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { selectedCompanyId } = useAuth();

  const [count, setCount] = useState(5);
  const [driverId, setDriverId] = useState('');
  const [drivers, setDrivers] = useState<User[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Fetch drivers whenever the navbar company selection changes
  useEffect(() => {
    const fetchDrivers = async () => {
      setLoadingDrivers(true);
      setDriverId('');
      try {
        const params = selectedCompanyId
          ? { role: 'driver' as const, company_id: selectedCompanyId }
          : { role: 'driver' as const };
        const data = await usersApi.listUsers(params);
        setDrivers(data);
      } catch (err) {
        console.error('Failed to load drivers:', err);
        setDrivers([]);
      } finally {
        setLoadingDrivers(false);
      }
    };

    fetchDrivers();
  }, [selectedCompanyId]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const payload: { count: number; company_id?: string; driver_id?: string } = { count };
      if (selectedCompanyId) {
        payload.company_id = selectedCompanyId;
      }
      if (driverId) {
        payload.driver_id = driverId;
      }

      const response = await tripsApi.generateDemoTrips(payload);
      const created = response.data.created;

      toast.success(
        created !== 1
          ? t('demoModal.successMessagePlural', { count: created })
          : t('demoModal.successMessage', { count: created })
      );
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || t('demoModal.errorMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-indigo-100">
              <svg
                className="w-5 h-5 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{t('demoModal.title')}</h2>
              <p className="text-xs text-indigo-600 font-medium uppercase tracking-wide">
                {t('demoModal.superAdminTool')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-5">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Scope banner */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
              <svg
                className="w-4 h-4 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              <span className="text-sm text-gray-600">
                {selectedCompanyId ? (
                  <>
                    {t('demoModal.scopeSelected').split('<strong>')[0]}
                    <strong className="text-gray-900">
                      {t('demoModal.scopeSelected')
                        .replace(/<\/?strong>/g, '')
                        .replace(/^[^<]*/, '')
                        .trim()}
                    </strong>
                  </>
                ) : (
                  <>
                    {t('demoModal.scopeAll').split('<strong>')[0]}
                    <strong className="text-gray-900">
                      {t('demoModal.scopeAll')
                        .replace(/<\/?strong>/g, '')
                        .replace(/^[^<]*/, '')
                        .trim()}
                    </strong>
                  </>
                )}
              </span>
            </div>

            {/* Number of trips */}
            <div>
              <label
                htmlFor="demo-trip-count"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('demoModal.numberOfTrips')}
              </label>
              <input
                id="demo-trip-count"
                type="number"
                value={count}
                onChange={(e) =>
                  setCount(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))
                }
                min={1}
                max={20}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-sm"
              />
              <p className="mt-1 text-xs text-gray-500">{t('demoModal.tripsRange')}</p>
            </div>

            {/* Driver selector */}
            <div>
              <label
                htmlFor="demo-trip-driver"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('demoModal.driver')}{' '}
                <span className="text-gray-400 font-normal">{t('demoModal.driverOptional')}</span>
              </label>
              {loadingDrivers ? (
                <div className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg bg-gray-50">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-600" />
                  <span className="text-sm text-gray-500">{t('demoModal.loadingDrivers')}</span>
                </div>
              ) : (
                <select
                  id="demo-trip-driver"
                  value={driverId}
                  onChange={(e) => setDriverId(e.target.value)}
                  disabled={drivers.length === 0}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  <option value="">{t('demoModal.autoPickDriver')}</option>
                  {drivers.map((driver) => (
                    <option key={driver._id} value={driver._id}>
                      {driver.name} — {driver.email}
                    </option>
                  ))}
                </select>
              )}
              <p className="mt-1 text-xs text-gray-500">
                {drivers.length === 0 && !loadingDrivers
                  ? t('demoModal.noDriversFound')
                  : t('demoModal.leaveBlank')}
              </p>
            </div>

            {/* Info notice */}
            <div className="flex gap-3 p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
              <svg
                className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-xs text-indigo-700">{t('demoModal.infoNotice')}</p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('demoModal.cancel')}
            </button>
            <button
              type="submit"
              disabled={submitting || loadingDrivers}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  {t('demoModal.generating')}
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                  {t('demoModal.generate')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
